import {EphemeralRealtimeService} from './core/services/ephemeral-realtime.service';
import {Component, Inject, OnDestroy, OnInit, Optional, PLATFORM_ID} from '@angular/core';
import {PrimeNGConfig} from 'primeng/api';
import {App as CapacitorApp} from '@capacitor/app';
import {ModalService} from './shared/modal/modal.service';
import {DOCUMENT, isPlatformBrowser} from "@angular/common";
import {AppTrackingTransparency} from "capacitor-plugin-app-tracking-transparency";
import {Capacitor} from "@capacitor/core";
import {SafeArea, initialize} from "@capacitor-community/safe-area";
import {Keyboard, KeyboardInfo} from '@capacitor/keyboard';
import {NavigationEnd, NavigationStart, Router} from "@angular/router";
import {filter} from "rxjs/operators";
import {SeoService} from "./core/services/seo.service";
import {TokenDataProviderService} from "./core/services/token-data-provider.service";
import {ChatDataProviderService} from "./features/chat/chat.data-provider.service";
import {TranslateService} from "@ngx-translate/core";
import {PushNotificationsService} from "./core/services/push-notifications.service";
import {NotificationDataProviderService} from "./core/services/notification.data-provider.service";
import {Subscription} from "rxjs";
import {EventSourceInterface} from "./core/interfaces/event-source.interface";
import {NetworkStatusService} from "./core/services/network-status.service";
import {ResponseNotificationService} from "./core/services/response-notification.service";
import {BackgroundUploadOrchestratorService} from "./core/services/background-upload-orchestrator.service";
import {TutorialOrchestratorService} from "./core/onboarding/tutorial-orchestrator.service";
import {REQUEST} from "./core/providers/http-context-providers";
import {MobileBackCoordinatorService} from './core/mobile/mobile-back-coordinator.service';
import {MobileExperienceDiagnosticsService} from './core/mobile/mobile-experience-diagnostics.service';
import {MobileExperienceShellService} from './core/mobile/mobile-experience-shell.service';
import type { Request } from "express";


initialize();

export const APP_COMPONENT_TEMPLATE = `
  <div id="app-root-content" class="app-root-content">
    <app-notification></app-notification>
    <app-loader></app-loader>
    <app-open-cta></app-open-cta>
    <router-outlet></router-outlet>
    <app-confirm-dialog></app-confirm-dialog>
    <app-modal></app-modal>
  </div>`;

@Component({
  selector: 'app-root',
  template: APP_COMPONENT_TEMPLATE,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly supportedUniversalLinkHosts = new Set([
    'watchthisgarage.com',
    'pl-pl.watchthisgarage.com',
    'de-de.watchthisgarage.com',
    'en-us.watchthisgarage.com',
    'test.watchthisgarage.com'
  ]);

  topbarTheme: string = 'dark';

  menuTheme: string = 'dark';

  layoutMode: string = 'dark';

  menuMode: string = 'overlay';

  inlineMenuPosition: string = 'top';

  inputStyle: string = 'outlined';

  ripple: boolean = true;

  isRTL: boolean = false;
  private unreadCountUserId: string | null = null;
  private unreadCountStreamSub: Subscription | null = null;
  private networkStatusSub: Subscription | null = null;
  private previousNetworkConnected: boolean | null = null;
  private readonly updateAppHeight = () => {
    if (!this.document?.documentElement || typeof window === 'undefined') {
      return;
    }
    const visualViewportHeight = typeof window !== 'undefined' && window.visualViewport
      ? window.visualViewport.height
      : 0;
    const viewportHeight = visualViewportHeight || window.innerHeight;
    this.document.documentElement.style.setProperty('--app-height', `${viewportHeight}px`);
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Optional() @Inject(REQUEST) private readonly request: Request | null,
    private readonly primengConfig: PrimeNGConfig,
    private readonly ms: ModalService,
    private readonly router: Router,
    private readonly seo: SeoService,
    private readonly translate: TranslateService,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly chatDPS: ChatDataProviderService,
    private readonly notificationDPS: NotificationDataProviderService,
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly networkStatus: NetworkStatusService,
    private readonly ephemeralRealtime: EphemeralRealtimeService,
    private readonly notifications: ResponseNotificationService,
    private readonly backgroundUploadOrchestrator: BackgroundUploadOrchestratorService,
    private readonly tutorialOrchestrator: TutorialOrchestratorService,
    private readonly mobileExperienceShell: MobileExperienceShellService,
    private readonly mobileBackCoordinator: MobileBackCoordinatorService,
    private readonly mobileExperienceDiagnostics: MobileExperienceDiagnosticsService,
  ) {
  }

  async ngOnInit() {
    this.primengConfig.ripple = true;
    this.mobileExperienceShell.initialize();
    this.mobileExperienceDiagnostics.initialize();
    this.updateAppHeight();
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('resize', this.updateAppHeight, {passive: true});
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.updateAppHeight, {passive: true});
      }
    }
    if (Capacitor.getPlatform() === 'ios') {
      await AppTrackingTransparency.requestPermission();
    }
    if (Capacitor.getPlatform() !== 'web') {
      this.pushNotificationsService.init();
      await this.backgroundUploadOrchestrator.init();
    }

    await SafeArea.enable({
      config: {
        customColorsForSystemBars: true,
        statusBarColor: '#00000000', // transparent
        statusBarContent: 'light',
        navigationBarColor: '#00000000', // transparent
        navigationBarContent: 'light',
      },
    });

    this.document.documentElement.style.setProperty('--padding-top-on-keyboard',  '0px');
    if (Capacitor.getPlatform() !=='web') {
      await Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
        if (Capacitor.getPlatform() === 'ios') {
          const computedStyle = getComputedStyle(this.document.documentElement);
          const safeAreaInsetTop = computedStyle.getPropertyValue('--safe-area-inset-top').trim();
          this.document.documentElement.style.setProperty('--padding-top-on-keyboard', safeAreaInsetTop);
        }
        this.document.documentElement.style.setProperty('--keyboard-height', info.keyboardHeight + 'px');
      });

      await Keyboard.addListener('keyboardWillHide', () => {
        this.document.documentElement.style.setProperty('--padding-top-on-keyboard', '0px');
        this.document.documentElement.style.setProperty('--keyboard-height', '0px');
      });
    }

    this.applyGlobalSeoState(this.router.url);

    if (isPlatformBrowser(this.platformId)) {
      this.ephemeralRealtime.initialize();
      this.tutorialOrchestrator.init();
      this.networkStatusSub = this.networkStatus.status$.subscribe((state) => {
        if (this.previousNetworkConnected === null) {
          this.previousNetworkConnected = state.connected;
          return;
        }
        if (this.previousNetworkConnected !== state.connected) {
          if (!state.connected) {
            this.notifications.emitWarning(this.translate.instant('app.network.offline'));
          } else {
            this.notifications.emitMessage(this.translate.instant('app.network.online'));
            void this.backgroundUploadOrchestrator.init();
          }
          this.previousNetworkConnected = state.connected;
        }
      });

      this.tokenDPS.user$.subscribe((user) => {
        const userId = user?.id ? user.id.toString() : null;
        if (!userId) {
          this.unreadCountStreamSub?.unsubscribe();
          this.unreadCountStreamSub = null;
          this.unreadCountUserId = null;
          return;
        }
        if (this.unreadCountUserId !== userId) {
          this.unreadCountUserId = userId;
          this.unreadCountStreamSub?.unsubscribe();
          this.unreadCountStreamSub = this.chatDPS.listenUnreadCount(userId, false)
            .subscribe((event: EventSourceInterface<{count: number}>) => {
              if (typeof event?.payload?.count === 'number') {
                this.chatDPS.setUnreadCount(event.payload.count);
              }
            });
        }
        this.chatDPS.getUnreadCount().subscribe((count) => this.chatDPS.setUnreadCount(count));
        this.notificationDPS.getUnreadCount().subscribe();
      });
      this.router.events.pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart)
      ).subscribe(() => {
        if (this.getCurrentHostname() !== 'test.watchthisgarage.com') {
          this.seo.clearRobots();
        }
      });
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.applyGlobalSeoState(this.router.url);
        this.applyResolvedTitle();
      });
      await CapacitorApp.addListener('backButton', async ({canGoBack}) => {
        console.log('back button pressed')
        if (await this.mobileBackCoordinator.handle()) {
          return;
        }
        if (this.ms.isVisible) {
          this.ms.close();
          return;
        }
        if (!canGoBack) {
          CapacitorApp.exitApp();
          return;
        }
        if (isPlatformBrowser(this.platformId)) {
          window.history.back();
        }
      });

      await CapacitorApp.addListener('appUrlOpen', async (event) => {
        await this.handleIncomingAppUrl(event?.url);
      });

      const launchUrl = await CapacitorApp.getLaunchUrl();
      await this.handleIncomingAppUrl(launchUrl?.url);

      await CapacitorApp.addListener('resume', () => {
        this.ephemeralRealtime.resume();
        void this.backgroundUploadOrchestrator.init();
      });

      document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;

        if (!target || !target.classList.contains('mention')) {
          return;
        }

        const type = target.getAttribute('data-type') || target.dataset.type;
        const slug = target.getAttribute('data-slug') || target.dataset.slug;
        const ownerSlug = target.getAttribute('data-owner-slug') || target.dataset.ownerSlug;
        if (type && slug) {
          event.preventDefault();
          let href: string;
          switch (type) {
            case 'profile': href = `/${slug}/${type}`; break;
            case 'project':
              href = ownerSlug ? `/${ownerSlug}/garage/${slug}` : `/garage/${slug}`;
              break;
            default: return;
          }
          this.router.navigateByUrl(href);
        }
      });
    }
  }

  private applyGlobalSeoState(currentUrl: string): void {
    const normalizedPath = this.normalizeSeoPath(currentUrl);
    this.seo.setHtmlLang(this.translate.currentLang || this.translate.defaultLang || 'en-US');
    this.seo.setLocaleAlternates(normalizedPath);
    this.seo.setCanonical(normalizedPath);
    if (this.getCurrentHostname() === 'test.watchthisgarage.com') {
      this.seo.setIndexable(false, false);
    }
  }

  private async handleIncomingAppUrl(url: string | undefined): Promise<void> {
    const target = this.resolveInAppUrl(url);
    if (!target) {
      return;
    }

    // An incoming link must not navigate behind an overlay. A wizard with a
    // draft can present its own close guard; in that case the user confirms or
    // cancels first and can open the link again afterwards.
    if (this.mobileBackCoordinator.handlerCount && await this.mobileBackCoordinator.handle()) {
      return;
    }

    if (this.ms.isVisible) {
      this.ms.close();
      return;
    }

    if (target.startsWith('/marketplace/payment/')) {
      this.ms.forceClose();
    }

    await this.router.navigateByUrl(target).catch(() => {});
  }

  private applyResolvedTitle(): void {
    let route = this.router.routerState.snapshot.root;
    let lastSnapshot = route;
    while (route) {
      lastSnapshot = route;
      route = route.firstChild || null;
    }

    const path = lastSnapshot.routeConfig?.path || '';
    const data = lastSnapshot.data || {};
    if (path === 'garage/:id') {
      const project = data['project'];
      const vehicleLabel = project?.vehicle?.fullVehicleIdentity || project?.alias || project?.slug || '';
      const fallbackLabel = this.translate.instant('common.project') === 'common.project'
        ? 'Project'
        : this.translate.instant('common.project');
      const title = vehicleLabel || fallbackLabel;
      this.seo.setTitle(title);
      return;
    }

    if (path.startsWith('garage')) {
      const user = data['user'];
      const garageLabel = this.translate.instant('common.garage') === 'common.garage'
        ? 'Garage'
        : this.translate.instant('common.garage');
      const ownerLabel = user?.username || user?.slug || '';
      const title = ownerLabel ? `${garageLabel} - ${ownerLabel}` : garageLabel;
      this.seo.setTitle(title);
    }
  }

  private resolveInAppUrl(url: string | undefined): string | null {
    if (!url) {
      return null;
    }

    try {
      const parsed = new URL(url);
      const protocol = parsed.protocol.toLowerCase();

      if (protocol === 'http:' || protocol === 'https:') {
        if (!this.supportedUniversalLinkHosts.has(parsed.hostname)) {
          return null;
        }

        return `${parsed.pathname || '/'}${parsed.search || ''}${parsed.hash || ''}`;
      }

      if (protocol !== 'com.cg.wtg.app:') {
        return null;
      }

      const candidatePath = `${parsed.host ? `/${parsed.host}` : ''}${parsed.pathname || ''}`;
      return `${candidatePath || '/'}${parsed.search || ''}${parsed.hash || ''}`;
    } catch {
      return null;
    }
  }

  private normalizeSeoPath(url: string): string {
    const [pathPart = '/'] = (url || '/').split('?');
    return pathPart || '/';
  }

  private getCurrentHostname(): string {
    const headerHost = this.request?.headers?.['x-forwarded-host']
      || this.request?.headers?.['x-original-host']
      || this.request?.headers?.host;
    const requestHost = Array.isArray(headerHost) ? headerHost[0] : headerHost;
    const resolvedHost = requestHost || this.document?.location?.host || this.document?.location?.hostname || '';

    return resolvedHost.split(':')[0]?.toLowerCase() || '';
  }

  ngOnDestroy(): void {
    this.networkStatusSub?.unsubscribe();
    this.unreadCountStreamSub?.unsubscribe();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.updateAppHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.updateAppHeight);
      }
    }
  }
}
